import React, { useEffect, useState } from 'react'
import { BiCopy, BiCheck, BiShareAlt } from 'react-icons/bi'
import { SiX } from 'react-icons/si'
import { BsFacebook, BsWhatsapp, BsLinkedin } from 'react-icons/bs'
import { HiOutlineMail } from 'react-icons/hi'
import Axios from 'axios'
import AffiliateShell from '../../layouts/AffiliateShell'
import { API_URL } from '../../config'

const DEFAULT_MESSAGE =
  "Organising events? You need to check out #Karamale. Sell tickets, track revenue, and see real-time sales - all in one place.\n\nI'm part of their affiliate program because it works. See for yourself 👉"

const STEPS = [
  {
    title: 'Share your link',
    body: 'Send it to event organisers, post it, or add it to your bio.',
  },
  {
    title: 'They join Karamale',
    body: 'Organisers who register through your link are tied to your account.',
  },
  {
    title: 'You earn',
    body: 'You earn a commission on their online ticket sales as their events go live.',
  },
]

const copyText = async (value) => {
  try {
    await navigator.clipboard.writeText(value)
    return true
  } catch (e) {
    // fallback for older browsers and non-secure contexts
    try {
      const ta = document.createElement('textarea')
      ta.value = value
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      return ok
    } catch (err) {
      return false
    }
  }
}

/* Neutral page. Red appears only in the one gradient card at the top. */
const PAGE_CSS = `
  .al { --ink: #111827; --body: #374151; --muted: #6b7280; --line: #e5e7eb; --soft: #f9fafb; color: var(--ink); width: 100%; min-width: 0; }
  .al *, .al *::before, .al *::after { box-sizing: border-box; }
  .al-page { width: 100%; padding: 20px 20px 48px; min-width: 0; }
  .al button:focus-visible, .al a:focus-visible, .al input:focus-visible, .al textarea:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }

  .al-title { font-size: 22px; font-weight: 700; letter-spacing: -0.2px; margin: 0; }
  .al-sub { color: var(--muted); font-size: 14px; margin: 4px 0 22px; max-width: 64ch; font-weight: 400; }

  /* ---- link card ---- */
  .al-hero {
    color: #fff;
    border-radius: 16px;
    padding: 28px 32px;
    display: grid;
    grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
    gap: 32px;
    align-items: center;
    background:
      radial-gradient(900px 300px at 100% 0%, rgba(255,255,255,.10), transparent 60%),
      linear-gradient(135deg, #4a0b14 0%, #7d1523 52%, #a51d2d 100%);
    box-shadow: 0 10px 30px -12px rgba(122, 21, 35, .55);
  }
  .al-hero-label { font-size: 13px; letter-spacing: .6px; text-transform: uppercase; opacity: .75; font-weight: 400; }
  .al-hero h2 { font-size: 24px; font-weight: 700; letter-spacing: -0.4px; margin: 6px 0 6px; color: #fff; }
  .al-hero p { margin: 0 0 18px; font-size: 14px; opacity: .8; max-width: 52ch; font-weight: 400; }
  .al-linkrow { display: flex; gap: 8px; }
  .al .al-input { flex: 1; min-width: 0; height: 44px; border: 1px solid rgba(255,255,255,.25); background: rgba(255,255,255,.12); color: #fff; -webkit-text-fill-color: #fff; caret-color: #fff; border-radius: 8px; padding: 0 14px; font-size: 14px; text-overflow: ellipsis; }
  .al .al-input:focus { background: rgba(255,255,255,.18); border-color: rgba(255,255,255,.5); outline: none; }
  .al .al-input::selection { background: rgba(255,255,255,.35); -webkit-text-fill-color: #fff; }
  .al-copy { flex: none; display: inline-flex; align-items: center; gap: 6px; height: 44px; border: 0; background: #fff; color: #4a0b14; font-weight: 600; font-size: 14px; border-radius: 8px; padding: 0 18px; cursor: pointer; transition: background .15s, transform .1s; }
  .al-copy:hover { background: #f3f4f6; }
  .al-copy:active { transform: scale(.97); }
  .al-hero-side { background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.14); border-radius: 12px; padding: 18px 20px; }
  .al-stat-num { font-size: 44px; font-weight: 700; line-height: 1; letter-spacing: -1px; font-variant-numeric: tabular-nums; }
  .al-stat-label { font-size: 14px; opacity: .8; margin-top: 8px; font-weight: 400; }

  /* ---- content ---- */
  .al-grid { display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr); gap: 20px; align-items: start; margin-top: 20px; }
  .al-col { display: flex; flex-direction: column; gap: 20px; min-width: 0; }
  .al-card { background: #fff; border: 1px solid var(--line); border-radius: 12px; padding: 22px; min-width: 0; }
  .al-h3 { font-size: 16px; font-weight: 600; margin: 0 0 4px; }
  .al-muted { color: var(--muted); font-size: 14px; margin: 0 0 16px; font-weight: 400; }

  .al-share { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; }
  .al-share a, .al-share button { display: flex; align-items: center; gap: 10px; padding: 11px 14px; border: 1px solid var(--line); border-radius: 8px; background: #fff; color: var(--ink); font-size: 14px; font-weight: 500; text-decoration: none; cursor: pointer; transition: border-color .15s, background .15s; }
  .al-share a:hover, .al-share button:hover { border-color: #9ca3af; background: var(--soft); color: var(--ink); text-decoration: none; }
  .al-share svg { flex: none; color: var(--muted); }

  .al-textarea { width: 100%; border: 1px solid var(--line); border-radius: 8px; padding: 12px 14px; font-size: 14px; line-height: 1.55; color: var(--body); background: var(--soft); resize: vertical; min-height: 140px; font-weight: 400; }
  .al-textarea:focus { border-color: #9ca3af; background: #fff; outline: none; }
  .al-hint { font-size: 13px; color: var(--muted); margin: 8px 0 0; word-break: break-all; font-weight: 400; }
  .al-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 16px; margin-top: 12px; }
  .al-btn-link { display: inline-flex; align-items: center; gap: 6px; background: none; border: 0; padding: 0; color: var(--ink); font-weight: 600; font-size: 14px; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; text-decoration-color: #d1d5db; }
  .al-btn-link:hover { text-decoration-color: var(--ink); }
  .al-btn-link.quiet { color: var(--muted); font-weight: 500; }

  .al-steps { list-style: none; margin: 0; padding: 0; }
  .al-steps li { position: relative; padding: 0 0 22px 44px; }
  .al-steps li:last-child { padding-bottom: 0; }
  .al-steps li::before { content: ''; position: absolute; left: 13px; top: 32px; bottom: 2px; width: 1px; background: var(--line); }
  .al-steps li:last-child::before { display: none; }
  .al-num { position: absolute; left: 0; top: 0; width: 28px; height: 28px; border-radius: 50%; background: var(--ink); color: #fff; font-weight: 600; font-size: 13px; display: flex; align-items: center; justify-content: center; }
  .al-steps strong { display: block; font-size: 14px; font-weight: 600; }
  .al-steps span { display: block; font-size: 13px; color: var(--muted); margin-top: 2px; font-weight: 400; }

  .al-skel { height: 190px; border-radius: 16px; background: linear-gradient(90deg, #f3f4f6 25%, #e9ebee 37%, #f3f4f6 63%); background-size: 400% 100%; animation: al-shine 1.4s ease infinite; }
  @keyframes al-shine { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }
  @media (prefers-reduced-motion: reduce) { .al-skel { animation: none; } .al-copy { transition: none; } }

  @media (max-width: 900px) {
    .al-grid { grid-template-columns: minmax(0, 1fr); }
    .al-hero { grid-template-columns: minmax(0, 1fr); padding: 22px; gap: 20px; }
  }
  @media (max-width: 520px) {
    .al-page { padding: 16px 12px 40px; }
    .al-linkrow { flex-direction: column; }
    .al-copy { justify-content: center; padding: 11px 18px; }
  }
`

const AffiliateLinkCard = () => {
  const [copied, setCopied] = useState(false)
  const [msgCopied, setMsgCopied] = useState(false)
  const [affiliateid, setAffiliateid] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [affiliateCount, setAffiliateCount] = useState(0)
  const [message, setMessage] = useState(DEFAULT_MESSAGE)

  useEffect(() => {
    const partnername = localStorage.getItem('partnername')
    const partnerid = localStorage.getItem('partnerid')
    document.title = `${partnername} - Affiliate link`

    if (!localStorage.getItem('customid')) {
      localStorage.setItem('customid', `${partnerid}${crypto.randomUUID()}`)
    }

    let cancelled = false

    Axios.get(
      `${API_URL}/apps/kscanner/webservices.php?query=RECRUITS&subquery=AFFILIATECOUNT&partnerid=${partnerid}`,
      { withCredentials: true },
    )
      .then((response) => {
        if (!cancelled) {
          setAffiliateCount(response.data.val === 1 ? response.data.affiliateCount : 0)
        }
      })
      .catch((error) => console.log(error))

    Axios.get(
      `${API_URL}/apps/kscanner/webservices.php?query=PARTNERDETAILS&partnerid=${partnerid}`,
      { withCredentials: true },
    )
      .then((response) => {
        if (!cancelled) setAffiliateid(response.data.affiliateid ?? '')
      })
      .catch((error) => console.log(error))
      .finally(() => {
        if (!cancelled) setLoaded(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const affiliateUrl = `${API_URL}?affiliateid=${affiliateid}`
  const trimmed = message.trim()
  const fullMessage = `${trimmed} ${affiliateUrl}`
  const enc = encodeURIComponent
  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share

  const shareLinks = [
    {
      name: 'WhatsApp',
      icon: <BsWhatsapp size={19} />,
      href: `https://wa.me/?text=${enc(fullMessage)}`,
    },
    {
      name: 'Facebook',
      icon: <BsFacebook size={19} />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(affiliateUrl)}`,
    },
    {
      name: 'X',
      icon: <SiX size={17} />,
      href: `https://x.com/share?text=${enc(trimmed)}&url=${enc(affiliateUrl)}`,
    },
    {
      name: 'LinkedIn',
      icon: <BsLinkedin size={19} />,
      href: `https://www.linkedin.com/feed/?shareActive=true&text=${enc(fullMessage)}`,
    },
    {
      name: 'Email',
      icon: <HiOutlineMail size={21} />,
      href: `mailto:?subject=${enc('Sell your event tickets on Karamale')}&body=${enc(fullMessage)}`,
    },
  ]

  const flashCopied = (setter) => {
    setter(true)
    setTimeout(() => setter(false), 2000)
  }

  const handleCopy = async () => {
    if (await copyText(affiliateUrl)) flashCopied(setCopied)
  }

  const handleCopyMessage = async () => {
    if (await copyText(fullMessage)) flashCopied(setMsgCopied)
  }

  const handleNativeShare = () => {
    navigator.share({ text: trimmed, url: affiliateUrl }).catch(() => {})
  }

  return (
    <AffiliateShell>
      <style>{PAGE_CSS}</style>
      <div className="al">
        <div className="al-page pt-4">
          <h1 className="al-title">Your affiliate link</h1>
          <p className="al-sub">
            Share it with event organisers. When they join Karamale through it, you earn from their
            online ticket sales.
          </p>

          {!loaded ? (
            <div className="al-skel" />
          ) : affiliateid === '' ? (
            <div className="al-card">
              <h3 className="al-h3">Your link isn't ready yet</h3>
              <p className="al-muted">
                We couldn't find an affiliate link for your account. Refresh the page, or contact
                support if this keeps happening.
              </p>
              <button
                type="button"
                className="al-btn-link"
                onClick={() => window.location.reload()}
              >
                Refresh the page
              </button>
            </div>
          ) : (
            <>
              <section className="al-hero" aria-label="Your affiliate link">
                <div>
                  <div className="al-hero-label">Affiliate link</div>
                  <h2>Invite organisers to Karamale</h2>
                  <p>Anyone who registers through this link is connected to your account.</p>
                  <div className="al-linkrow">
                    <input
                      className="al-input"
                      type="text"
                      value={affiliateUrl}
                      readOnly
                      onFocus={(e) => e.target.select()}
                      aria-label="Your affiliate link"
                    />
                    <button type="button" className="al-copy" onClick={handleCopy}>
                      {copied ? <BiCheck size={18} /> : <BiCopy size={18} />}
                      {copied ? 'Copied' : 'Copy link'}
                    </button>
                  </div>
                </div>
                <div className="al-hero-side">
                  <div className="al-stat-num">{affiliateCount}</div>
                  <div className="al-stat-label">
                    {Number(affiliateCount) === 1 ? 'organiser has' : 'organisers have'} joined
                    through your link
                  </div>
                </div>
              </section>

              <div className="al-grid">
                <div className="al-col">
                  <div className="al-card">
                    <h3 className="al-h3">Share</h3>
                    <p className="al-muted">
                      Each option opens with your message below, ready to send.
                    </p>
                    <div className="al-share">
                      {shareLinks.map((s) => (
                        <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer">
                          {s.icon}
                          {s.name}
                        </a>
                      ))}
                      {canNativeShare && (
                        <button type="button" onClick={handleNativeShare}>
                          <BiShareAlt size={19} />
                          More options
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="al-card">
                    <h3 className="al-h3">Your message</h3>
                    <p className="al-muted">
                      Edit it to sound like you. Your link is added at the end.
                    </p>
                    <textarea
                      className="al-textarea"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      aria-label="Your share message"
                    />
                    <p className="al-hint">{affiliateUrl}</p>
                    <div className="al-actions">
                      <button type="button" className="al-btn-link" onClick={handleCopyMessage}>
                        {msgCopied ? <BiCheck size={16} /> : <BiCopy size={16} />}
                        {msgCopied ? 'Copied' : 'Copy message'}
                      </button>
                      {message !== DEFAULT_MESSAGE && (
                        <button
                          type="button"
                          className="al-btn-link quiet"
                          onClick={() => setMessage(DEFAULT_MESSAGE)}
                        >
                          Reset to the original
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="al-col">
                  <div className="al-card">
                    <h3 className="al-h3" style={{ marginBottom: 18 }}>
                      How it works
                    </h3>
                    <ol className="al-steps">
                      {STEPS.map((step, i) => (
                        <li key={step.title}>
                          <div className="al-num">{i + 1}</div>
                          <strong>{step.title}</strong>
                          <span>{step.body}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AffiliateShell>
  )
}

export default AffiliateLinkCard
